import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Calendar, Clock, Package, MapPin, User, Mail, Phone, Building2, Truck, FileText, AlertCircle, CheckCircle, Send } from 'lucide-react';
import axios from 'axios';
import { PriceCalculator } from './PriceCalculator';

const MessageForm = () => {
  const [messageData, setMessageData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });
  const [messageLoading, setMessageLoading] = useState(false);
  const [messageResult, setMessageResult] = useState(null);

  const handleMessageChange = (field, value) => {
    setMessageData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMessageSubmit = async (e) => {
    e.preventDefault();
    setMessageLoading(true);
    setMessageResult(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/contact`,
        messageData,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setMessageResult({
          success: true,
          message: 'Message sent successfully! We\'ll get back to you within 24 hours.'
        });
        
        // Reset form
        setMessageData({
          name: '',
          email: '',
          phone: '',
          company: '',
          subject: '',
          message: ''
        });
      } else {
        setMessageResult({
          success: false,
          message: response.data.message || 'There was an error sending your message. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessageResult({
        success: false,
        message: 'There was an error sending your message. Please try again or call us directly.'
      });
    } finally {
      setMessageLoading(false);
    }
  };

  return (
    <>
      {/* Message Result */}
      {messageResult && (
        <Card className={`mb-6 shadow-lg border-0 ${messageResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              {messageResult.success ? (
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
              )}
              <p className={messageResult.success ? 'text-green-800' : 'text-red-800'}>
                {messageResult.message}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Message Form */}
      <form onSubmit={handleMessageSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="msg_name" className="text-sm font-semibold text-slate-700 flex items-center">
              <User className="h-4 w-4 mr-2 text-emerald-500" />
              Your Name *
            </Label>
            <Input
              id="msg_name"
              type="text"
              value={messageData.name}
              onChange={(e) => handleMessageChange('name', e.target.value)}
              required
              className="h-12 border-2 border-slate-200 focus:border-emerald-500 rounded-lg transition-colors"
              placeholder="Enter your name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="msg_email" className="text-sm font-semibold text-slate-700 flex items-center">
              <Mail className="h-4 w-4 mr-2 text-emerald-500" />
              Email Address *
            </Label>
            <Input
              id="msg_email"
              type="email"
              value={messageData.email}
              onChange={(e) => handleMessageChange('email', e.target.value)}
              required
              className="h-12 border-2 border-slate-200 focus:border-emerald-500 rounded-lg transition-colors"
              placeholder="your@email.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="msg_phone" className="text-sm font-semibold text-slate-700 flex items-center">
              <Phone className="h-4 w-4 mr-2 text-slate-400" />
              Phone Number
            </Label>
            <Input
              id="msg_phone"
              type="tel"
              value={messageData.phone}
              onChange={(e) => handleMessageChange('phone', e.target.value)}
              className="h-12 border-2 border-slate-200 focus:border-slate-300 rounded-lg transition-colors"
              placeholder="+44 7*** *** ***"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="msg_company" className="text-sm font-semibold text-slate-700 flex items-center">
              <Building2 className="h-4 w-4 mr-2 text-slate-400" />
              Company (Optional)
            </Label>
            <Input
              id="msg_company"
              type="text"
              value={messageData.company}
              onChange={(e) => handleMessageChange('company', e.target.value)}
              className="h-12 border-2 border-slate-200 focus:border-slate-300 rounded-lg transition-colors"
              placeholder="Your company name"
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="msg_subject" className="text-sm font-semibold text-slate-700 flex items-center">
            <FileText className="h-4 w-4 mr-2 text-emerald-500" />
            Subject *
          </Label>
          <Input
            id="msg_subject"
            type="text"
            value={messageData.subject}
            onChange={(e) => handleMessageChange('subject', e.target.value)}
            required
            className="h-12 border-2 border-slate-200 focus:border-emerald-500 rounded-lg transition-colors"
            placeholder="What can we help you with?"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="msg_message" className="text-sm font-semibold text-slate-700 flex items-center">
            <Mail className="h-4 w-4 mr-2 text-emerald-500" />
            Your Message *
          </Label>
          <textarea
            id="msg_message"
            value={messageData.message}
            onChange={(e) => handleMessageChange('message', e.target.value)}
            required
            rows={4}
            className="w-full px-4 py-3 border-2 border-slate-200 focus:border-emerald-500 rounded-lg shadow-sm focus:outline-none transition-colors resize-none"
            placeholder="Please describe your question or how we can help you..."
          />
        </div>

        <div className="pt-4">
          <Button
            type="submit"
            disabled={messageLoading}
            className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          >
            {messageLoading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Sending Message...</span>
              </div>
            ) : (
              <div className="flex items-center justify-center space-x-2">
                <Send className="h-5 w-5" />
                <span>Send Message Now</span>
              </div>
            )}
          </Button>
        </div>
      </form>
    </>
  );
};

export const BookDelivery = () => {
  const [formData, setFormData] = useState({
    customer_name: '',
    email: '',
    phone: '',
    company: '',
    pickup_address: '',
    pickup_postcode: '',
    pickup_date: '',
    pickup_time: '',
    delivery_address: '',
    delivery_postcode: '',
    delivery_date: '',
    delivery_time: '',
    vehicle_type: '',
    package_description: '',
    special_instructions: '',
    urgent: false
  });

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const formRef = useRef(null);

  // Scroll to top when component mounts (when customers click Get Quote)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const scrollToForm = () => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const vehicleTypes = [
    'Small Van',
    'SWB Van',
    'MWB Van',
    'XLWB Van',
    'Luton Van',
    '7.5 Tonne Truck'
  ];

  // Generate time slots
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 6; hour <= 22; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(time);
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Get today's date and tomorrow's date for date inputs
  const today = new Date().toISOString().split('T')[0];
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split('T')[0];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-set delivery date if not set and pickup date is chosen
    if (field === 'pickup_date' && !formData.delivery_date) {
      setFormData(prev => ({
        ...prev,
        delivery_date: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders`,
        formData,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setResult({
          success: true,
          message: response.data.message,
          quote_id: response.data.quote_id
        });
        
        // Reset form
        setFormData({
          customer_name: '',
          email: '',
          phone: '',
          company: '',
          pickup_address: '',
          pickup_postcode: '',
          pickup_date: '',
          pickup_time: '',
          delivery_address: '',
          delivery_postcode: '',
          delivery_date: '',
          delivery_time: '',
          vehicle_type: '',
          package_description: '',
          special_instructions: '',
          urgent: false
        });
      } else {
        setResult({
          success: false,
          message: response.data.message || 'There was an error booking your delivery. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error booking delivery:', error);
      setResult({
        success: false,
        message: 'There was an error booking your delivery. Please try again or call us directly.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="min-h-screen py-12 bg-gradient-to-br from-slate-50 via-white to-emerald-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(16,185,129,0.1),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(59,130,246,0.08),transparent_50%)]" />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-100 text-emerald-800 text-sm font-medium mb-6">
            <Package className="h-4 w-4 mr-2" />
            <span>Book Your Delivery</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            Schedule Your Same-Day Delivery
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Fill out the form below to book your delivery. You'll receive a tracking ID immediately 
            and can monitor your delivery status in real-time.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Result Message */}
          {result && (
            <Card className={`mb-8 shadow-xl border-0 ${result.success ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  {result.success ? (
                    <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-1" />
                  ) : (
                    <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
                  )}
                  <div>
                    <h3 className={`text-xl font-bold mb-3 ${result.success ? 'text-blue-900' : 'text-red-900'}`}>
                      {result.success ? '✅ Thank You! We Have Received Your Quote Request' : 'Request Failed'}
                    </h3>
                    {result.success ? (
                      <div className="space-y-4">
                        <div className="bg-blue-100 p-4 rounded-lg border border-blue-200">
                          <h4 className="font-semibold text-blue-900 mb-2">📧 Quote Request Confirmation</h4>
                          <p className="text-blue-800 text-sm leading-relaxed">
                            Your quote request has been successfully submitted! We have received all your delivery details and 
                            will review them shortly. A confirmation email has been sent to your inbox.
                          </p>
                        </div>
                        
                        <div className="bg-white p-4 rounded-lg border border-blue-200">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-blue-900">🏷️ Your Quote Reference</h4>
                            <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">Save this reference</span>
                          </div>
                          <p className="text-3xl font-bold font-mono text-blue-900 tracking-wider text-center bg-blue-50 py-3 rounded-lg mb-3">
                            {result.quote_id}
                          </p>
                          <p className="text-xs text-blue-700 text-center">
                            Use this reference when contacting us about your quote
                          </p>
                        </div>

                        <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                          <h4 className="font-semibold text-emerald-900 mb-2">📱 What Happens Next?</h4>
                          <ul className="text-emerald-800 text-sm space-y-1">
                            <li>• Our team will review your delivery requirements</li>
                            <li>• You'll receive a detailed <strong>invoice with pricing</strong> within <strong>24 hours</strong></li>
                            <li>• The invoice will include our company details and cost breakdown</li>
                            <li>• Once you approve the quote, we'll confirm your booking</li>
                            <li>• A <strong>tracking ID</strong> will be generated to monitor your delivery</li>
                          </ul>
                        </div>

                        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                          <h4 className="font-semibold text-yellow-900 mb-2">📞 Need Help?</h4>
                          <p className="text-yellow-800 text-sm">
                            Questions about your quote? Call us at{' '}
                            <a href="tel:01344508095" className="font-bold text-yellow-900 underline">
                              01344 508095
                            </a>
                            {' '}or email{' '}
                            <a href="mailto:customersupport@aisexpress.co.uk" className="font-bold text-yellow-900 underline">
                              customersupport@aisexpress.co.uk
                            </a>
                          </p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 pt-2">
                          <Button 
                            onClick={() => setResult(null)}
                            className="bg-blue-600 hover:bg-blue-700 flex items-center justify-center space-x-2"
                          >
                            <Package className="h-4 w-4" />
                            <span>Request Another Quote</span>
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-red-800">{result.message}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Customer Price Calculator */}
          <div className="mb-10">
            <PriceCalculator onBookNow={scrollToForm} />
          </div>

          {/* Booking Form */}
          <Card className="shadow-xl border-0 bg-white" ref={formRef}>
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-8">
                
                {/* Customer Information */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 rounded-2xl border-l-4 border-blue-500 shadow-lg">
                  <div className="flex items-center justify-center mb-8">
                    <div className="flex items-center bg-white px-6 py-3 rounded-full shadow-md">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 mr-3">
                        <User className="h-5 w-5 text-white" />
                      </div>
                      <h2 className="text-2xl font-bold text-slate-900">Customer Information</h2>
                    </div>
                  </div>
                  
                  <div className="bg-white p-6 rounded-xl shadow-md space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="customer_name" className="text-sm font-semibold text-slate-700 flex items-center">
                          <User className="h-4 w-4 mr-2 text-blue-500" />
                          Full Name *
                        </Label>
                        <Input
                          id="customer_name"
                          type="text"
                          value={formData.customer_name}
                          onChange={(e) => handleInputChange('customer_name', e.target.value)}
                          required
                          className="h-12 border-2 border-slate-200 focus:border-blue-500 rounded-lg transition-colors"
                          placeholder="Enter your full name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-sm font-semibold text-slate-700 flex items-center">
                          <Mail className="h-4 w-4 mr-2 text-blue-500" />
                          Email Address *
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          required
                          className="h-12 border-2 border-slate-200 focus:border-blue-500 rounded-lg transition-colors"
                          placeholder="your@email.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-sm font-semibold text-slate-700 flex items-center">
                          <Phone className="h-4 w-4 mr-2 text-blue-500" />
                          Phone Number *
                        </Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          required
                          className="h-12 border-2 border-slate-200 focus:border-blue-500 rounded-lg transition-colors"
                          placeholder="+44 7*** *** ***"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company" className="text-sm font-semibold text-slate-700 flex items-center">
                          <Building2 className="h-4 w-4 mr-2 text-slate-400" />
                          Company (Optional)
                        </Label>
                        <Input
                          id="company"
                          type="text"
                          value={formData.company}
                          onChange={(e) => handleInputChange('company', e.target.value)}
                          className="h-12 border-2 border-slate-200 focus:border-slate-300 rounded-lg transition-colors"
                          placeholder="Your company name"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pickup Details */}
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-8 rounded-2xl border-l-4 border-orange-500 shadow-lg">
                  <div className="flex items-center justify-center mb-8">
                    <div className="flex items-center bg-white px-6 py-3 rounded-full shadow-md">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-orange-600 mr-3">
                        <MapPin className="h-5 w-5 text-white" />
                      </div>
                      <h2 className="text-2xl font-bold text-slate-900">Pickup Details</h2>
                    </div>
                  </div>
                  
                  <div className="bg-white p-6 rounded-xl shadow-md space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="md:col-span-2 space-y-2">
                        <Label htmlFor="pickup_address" className="text-sm font-semibold text-slate-700 flex items-center">
                          <MapPin className="h-4 w-4 mr-2 text-orange-500" />
                          Pickup Address *
                        </Label>
                        <Input
                          id="pickup_address"
                          type="text"
                          value={formData.pickup_address}
                          onChange={(e) => handleInputChange('pickup_address', e.target.value)}
                          required
                          className="h-12 border-2 border-slate-200 focus:border-orange-500 rounded-lg transition-colors"
                          placeholder="Full pickup address including street number"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="pickup_postcode" className="text-sm font-semibold text-slate-700 flex items-center">
                          <MapPin className="h-4 w-4 mr-2 text-orange-500" />
                          Pickup Postcode *
                        </Label>
                        <Input
                          id="pickup_postcode"
                          type="text"
                          value={formData.pickup_postcode}
                          onChange={(e) => handleInputChange('pickup_postcode', e.target.value.toUpperCase())}
                          required
                          className="h-12 border-2 border-slate-200 focus:border-orange-500 rounded-lg transition-colors"
                          placeholder="RG1 2AB"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="pickup_date" className="text-sm font-semibold text-slate-700 flex items-center">
                          <Calendar className="h-4 w-4 mr-2 text-orange-500" />
                          Pickup Date *
                        </Label>
                        <Input
                          id="pickup_date"
                          type="date"
                          value={formData.pickup_date}
                          onChange={(e) => handleInputChange('pickup_date', e.target.value)}
                          required
                          min={today}
                          className="h-12 border-2 border-slate-200 focus:border-orange-500 rounded-lg transition-colors"
                        />
                      </div>
                      <div className="md:col-span-2 space-y-2">
                        <Label htmlFor="pickup_time" className="text-sm font-semibold text-slate-700 flex items-center">
                          <Clock className="h-4 w-4 mr-2 text-orange-500" />
                          Preferred Pickup Time *
                        </Label>
                        <Select 
                          value={formData.pickup_time} 
                          onValueChange={(value) => handleInputChange('pickup_time', value)}
                          required
                        >
                          <SelectTrigger className="h-12 border-2 border-slate-200 focus:border-orange-500 rounded-lg transition-colors">
                            <SelectValue placeholder="Select pickup time" />
                          </SelectTrigger>
                          <SelectContent>
                            {timeSlots.map((time) => (
                              <SelectItem key={time} value={time}>
                                {time}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Delivery Details */}
                <div className="bg-gradient-to-r from-emerald-50 to-green-50 p-8 rounded-2xl border-l-4 border-emerald-500 shadow-lg">
                  <div className="flex items-center justify-center mb-8">
                    <div className="flex items-center bg-white px-6 py-3 rounded-full shadow-md">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 mr-3">
                        <MapPin className="h-5 w-5 text-white" />
                      </div>
                      <h2 className="text-2xl font-bold text-slate-900">Delivery Details</h2>
                    </div>
                  </div>
                  
                  <div className="bg-white p-6 rounded-xl shadow-md space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="md:col-span-2 space-y-2">
                        <Label htmlFor="delivery_address" className="text-sm font-semibold text-slate-700 flex items-center">
                          <MapPin className="h-4 w-4 mr-2 text-emerald-500" />
                          Delivery Address *
                        </Label>
                        <Input
                          id="delivery_address"
                          type="text"
                          value={formData.delivery_address}
                          onChange={(e) => handleInputChange('delivery_address', e.target.value)}
                          required
                          className="h-12 border-2 border-slate-200 focus:border-emerald-500 rounded-lg transition-colors"
                          placeholder="Full delivery address including street number"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="delivery_postcode" className="text-sm font-semibold text-slate-700 flex items-center">
                          <MapPin className="h-4 w-4 mr-2 text-emerald-500" />
                          Delivery Postcode *
                        </Label>
                        <Input
                          id="delivery_postcode"
                          type="text"
                          value={formData.delivery_postcode}
                          onChange={(e) => handleInputChange('delivery_postcode', e.target.value.toUpperCase())}
                          required
                          className="h-12 border-2 border-slate-200 focus:border-emerald-500 rounded-lg transition-colors"
                          placeholder="SL1 3CD"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="delivery_date" className="text-sm font-semibold text-slate-700 flex items-center">
                          <Calendar className="h-4 w-4 mr-2 text-slate-400" />
                          Delivery Date <span className="text-red-500 ml-1">*</span>
                        </Label>
                        <Input
                          id="delivery_date"
                          type="date"
                          value={formData.delivery_date}
                          onChange={(e) => handleInputChange('delivery_date', e.target.value)}
                          min={formData.pickup_date || today}
                          required
                          className="h-12 border-2 border-slate-200 focus:border-emerald-500 rounded-lg transition-colors"
                        />
                      </div>
                      <div className="md:col-span-2 space-y-2">
                        <Label htmlFor="delivery_time" className="text-sm font-semibold text-slate-700 flex items-center">
                          <Clock className="h-4 w-4 mr-2 text-slate-400" />
                          Estimated Delivery Time (Optional)
                        </Label>
                        <Select 
                          value={formData.delivery_time} 
                          onValueChange={(value) => handleInputChange('delivery_time', value)}
                        >
                          <SelectTrigger className="h-12 border-2 border-slate-200 focus:border-slate-300 rounded-lg transition-colors">
                            <SelectValue placeholder="Select estimated delivery time" />
                          </SelectTrigger>
                          <SelectContent>
                            {timeSlots.map((time) => (
                              <SelectItem key={time} value={time}>
                                {time}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Package Information */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-8 rounded-2xl border-l-4 border-purple-500 shadow-lg">
                  <div className="flex items-center justify-center mb-8">
                    <div className="flex items-center bg-white px-6 py-3 rounded-full shadow-md">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-purple-600 mr-3">
                        <Package className="h-5 w-5 text-white" />
                      </div>
                      <h2 className="text-2xl font-bold text-slate-900">Package Information</h2>
                    </div>
                  </div>
                  
                  <div className="bg-white p-6 rounded-xl shadow-md space-y-6">
                    <div className="grid grid-cols-1 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="vehicle_type" className="text-sm font-semibold text-slate-700 flex items-center">
                          <Truck className="h-4 w-4 mr-2 text-purple-500" />
                          Vehicle Type Required *
                        </Label>
                        <Select 
                          value={formData.vehicle_type} 
                          onValueChange={(value) => handleInputChange('vehicle_type', value)}
                          required
                        >
                          <SelectTrigger className="h-12 border-2 border-slate-200 focus:border-purple-500 rounded-lg transition-colors">
                            <SelectValue placeholder="Select vehicle type" />
                          </SelectTrigger>
                          <SelectContent>
                            {vehicleTypes.map((type) => (
                              <SelectItem key={type} value={type}>
                                {type}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="package_description" className="text-sm font-semibold text-slate-700 flex items-center">
                          <Package className="h-4 w-4 mr-2 text-purple-500" />
                          Package Description *
                        </Label>
                        <Input
                          id="package_description"
                          type="text"
                          value={formData.package_description}
                          onChange={(e) => handleInputChange('package_description', e.target.value)}
                          required
                          className="h-12 border-2 border-slate-200 focus:border-purple-500 rounded-lg transition-colors"
                          placeholder="Brief description of items being delivered"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="special_instructions" className="text-sm font-semibold text-slate-700 flex items-center">
                          <FileText className="h-4 w-4 mr-2 text-slate-400" />
                          Special Instructions (Optional)
                        </Label>
                        <textarea
                          id="special_instructions"
                          value={formData.special_instructions}
                          onChange={(e) => handleInputChange('special_instructions', e.target.value)}
                          rows={3}
                          className="w-full px-4 py-3 border-2 border-slate-200 focus:border-slate-300 rounded-lg shadow-sm focus:outline-none transition-colors resize-none"
                          placeholder="Any special handling requirements or delivery notes..."
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Urgent Delivery Option */}
                <div className="border-t border-slate-200 pt-6">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="urgent"
                      checked={formData.urgent}
                      onCheckedChange={(checked) => handleInputChange('urgent', checked)}
                    />
                    <div>
                      <Label htmlFor="urgent" className="text-base font-semibold text-slate-900">
                        Urgent Delivery (Additional charges may apply)
                      </Label>
                      <p className="text-sm text-slate-600">
                        Check this for priority handling and faster delivery times
                      </p>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="border-t border-slate-200 pt-8">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-4 text-lg font-semibold"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Booking Your Delivery...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center space-x-2">
                        <Truck className="h-5 w-5" />
                        <span>Book Delivery Now</span>
                      </div>
                    )}
                  </Button>
                  
                  <div className="mt-6 text-center text-sm text-slate-600">
                    <p>
                      By booking a delivery, you agree to our{' '}
                      <a href="/terms" className="text-emerald-600 hover:text-emerald-700 underline">
                        Terms & Conditions
                      </a>
                      {' '}and{' '}
                      <a href="/privacy" className="text-emerald-600 hover:text-emerald-700 underline">
                        Privacy Policy
                      </a>
                    </p>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Send us a Message Section */}
          <Card className="mt-8 shadow-xl border-0 bg-gradient-to-r from-emerald-50 to-teal-50 border-l-4 border-emerald-500">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <div className="flex items-center justify-center mb-6">
                  <div className="flex items-center bg-white px-6 py-3 rounded-full shadow-md">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 mr-3">
                      <Mail className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900">Send us a Message</h3>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border border-emerald-100">
                  <p className="text-slate-700 max-w-2xl mx-auto leading-relaxed">
                    💬 Have questions about your booking or need assistance? Send us a message and we'll get back to you within <span className="font-semibold text-emerald-700">24 hours</span>.
                  </p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md">
                <MessageForm />
              </div>
            </CardContent>
          </Card>

          {/* Help Section */}
          <Card className="mt-8 shadow-lg border-0 bg-gradient-to-br from-slate-50 to-emerald-50">
            <CardContent className="p-8 text-center">
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                Need Help with Your Booking?
              </h3>
              <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
                Our customer service team is available 24/7 to help with bookings, 
                provide quotes, or answer any questions about our delivery services.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => window.open('tel:01344508095', '_self')}
                  className="bg-emerald-600 hover:bg-emerald-700 flex items-center space-x-2 px-6 py-3"
                >
                  <Phone className="h-4 w-4" />
                  <span>Call 01344 508095</span>
                </Button>
                <Button 
                  onClick={() => window.location.href = '/#contact'}
                  variant="outline"
                  className="flex items-center space-x-2 px-6 py-3"
                >
                  <Mail className="h-4 w-4" />
                  <span>Send Message</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};